/**
 * Session state and the auth context.
 *
 * Permission checks here are UX only -- they decide what to render. The
 * backend is the sole enforcement point (data-privacy skill).
 */
import { QueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { api, refreshSession, unwrap } from "@/lib/api/client";
import {
  millisUntilExpiry,
  setAccessToken,
  setSessionRefreshedHandler,
  setUnauthorizedHandler,
} from "@/lib/auth/token-store";
import type { components } from "@/lib/api/generated/schema";

export type SessionOut = components["schemas"]["SessionOut"];
export type Role = components["schemas"]["Role"];
export type Permission = components["schemas"]["Permission"];
export type Membership = components["schemas"]["MembershipOut"];

interface SessionContextValue {
  session: SessionOut | null;
  status: "loading" | "authenticated" | "anonymous";
  signIn: (
    email: string,
    password: string,
    mahalluId?: string,
  ) => Promise<SessionOut>;
  signOut: () => Promise<void>;
  switchMahallu: (mahalluId: string) => Promise<void>;
  changePassword: (current: string, next: string) => Promise<void>;
  hasRole: (...roles: Role[]) => boolean;
  /**
   * Whether this session may do something, per the server's own matrix.
   *
   * The session carries its effective permissions, so nothing here has to
   * keep a second copy of the role table -- a copy that was never written,
   * which is why the sidebar used to offer a VIEWER four screens that all
   * answered 403. Hiding a control is UX; the backend still refuses.
   */
  can: (...permissions: Permission[]) => boolean;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({
  children,
  queryClient,
}: {
  children: ReactNode;
  queryClient: QueryClient;
}) {
  const [session, setSession] = useState<SessionOut | null>(null);
  const [status, setStatus] =
    useState<SessionContextValue["status"]>("loading");

  const apply = useCallback((next: SessionOut) => {
    if (next.access_token) setAccessToken(next.access_token, next.expires_in);
    setSession(next);
    setStatus("authenticated");
  }, []);

  const clear = useCallback(() => {
    setAccessToken(null);
    setSession(null);
    setStatus("anonymous");
    // clear(), not invalidateQueries: the previous tenant's data must leave
    // memory entirely, not merely be marked stale.
    queryClient.clear();
  }, [queryClient]);

  useEffect(() => {
    setUnauthorizedHandler(clear);
    // A refresh started by the API client produces a whole session, not just a
    // token: roles and memberships can change while a session is live.
    setSessionRefreshedHandler((next) => apply(next as SessionOut));
    return () => {
      setUnauthorizedHandler(null);
      setSessionRefreshedHandler(null);
    };
  }, [clear, apply]);

  /**
   * Renew before the token dies, rather than after.
   *
   * Waiting for a 401 works -- the client retries -- but it means every screen
   * opened after an idle period pays for a round of failed requests first. A
   * minute of headroom covers a slow network and a little clock skew.
   */
  useEffect(() => {
    if (status !== "authenticated") return;
    const headroom = 60_000;
    const delay = Math.max(5_000, millisUntilExpiry() - headroom);
    const timer = setTimeout(() => void refreshSession(), delay);
    return () => clearTimeout(timer);
  }, [status, session?.access_token]);

  /**
   * A backgrounded tab's timers are throttled, and a sleeping laptop's stop
   * altogether, so the token is often already dead by the time the user looks
   * at the screen again. Renew on the way back in.
   */
  useEffect(() => {
    if (status !== "authenticated") return;
    const onVisible = () => {
      if (
        document.visibilityState === "visible" &&
        millisUntilExpiry() < 60_000
      ) {
        void refreshSession();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [status]);

  /**
   * Rehydrate on load: the access token lives only in memory, but the HttpOnly
   * refresh cookie survives a reload.
   *
   * Through `refreshSession()`, NOT a direct `api.POST` to the same endpoint.
   * Refresh tokens rotate, and presenting a rotated one is how the server
   * detects theft -- it revokes the whole chain. This effect runs twice on
   * mount under StrictMode, so a direct call fired two refreshes carrying the
   * same cookie; whenever they overlapped, the second presented a token the
   * first had already rotated, the chain was revoked, and the reload the user
   * had just done signed them out. Intermittently, which is the worst way for
   * it to happen.
   *
   * `refreshSession()` keeps one request in flight and hands the same promise
   * to every caller, which is exactly what both invocations need.
   */
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const restored = await refreshSession();
      if (cancelled) return;
      if (restored) {
        apply(restored as SessionOut);
      } else {
        setStatus("anonymous");
        setAccessToken(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apply]);

  const signIn = useCallback(
    async (email: string, password: string, mahalluId?: string) => {
      const next = unwrap(
        await api.POST("/api/v1/auth/login", {
          body: { email, password, mahallu_id: mahalluId ?? null },
        }),
      );
      queryClient.clear();
      apply(next);
      return next;
    },
    [apply, queryClient],
  );

  const signOut = useCallback(async () => {
    try {
      await api.POST("/api/v1/auth/logout", {});
    } finally {
      clear();
    }
  }, [clear]);

  const switchMahallu = useCallback(
    async (mahalluId: string) => {
      const next = unwrap(
        await api.POST("/api/v1/auth/switch-mahallu", {
          body: { mahallu_id: mahalluId },
        }),
      );
      // Every cached query is scoped to the previous mahallu.
      queryClient.clear();
      apply(next);
    },
    [apply, queryClient],
  );

  /**
   * Change your own password.
   *
   * The server bumps the token version and revokes every refresh token, which
   * kills the session this request was made from as well -- so it hands back a
   * fresh one, and that has to be applied here. Treating this like an ordinary
   * mutation would sign the user out of the tab they just used to change it.
   *
   * The cache is deliberately not cleared: the tenant has not changed, and
   * blanking every screen behind the dialog reads as an error.
   */
  const changePassword = useCallback(
    async (current: string, next: string) => {
      const session = unwrap(
        await api.POST("/api/v1/auth/password", {
          body: { current_password: current, new_password: next },
        }),
      );
      apply(session);
    },
    [apply],
  );

  const value = useMemo<SessionContextValue>(
    () => ({
      session,
      status,
      signIn,
      signOut,
      switchMahallu,
      changePassword,
      hasRole: (...roles: Role[]) =>
        roles.some((role) => session?.roles.includes(role) ?? false),
      can: (...permissions: Permission[]) => {
        // Read defensively. The API and the web app deploy independently, so
        // there is a window where this client is talking to a build that does
        // not send the field yet -- and a sidebar that throws during those
        // minutes is worse than one that briefly offers less.
        const granted = session?.permissions;
        if (!granted) return false;
        return permissions.some((permission) => granted.includes(permission));
      },
    }),
    [session, status, signIn, signOut, switchMahallu, changePassword],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used inside SessionProvider");
  return ctx;
}
